/**
 * Livrables Tangibles par Tactique - Cadre Référentiel BZ
 * 
 * Cette structure extrait tous les livrables de toutes les tactiques
 * et les organise pour l'affichage dans l'onglet "Livrables Tangibles"
 */

import { tactiques, piliers } from './cadreReferentielBZ';

export interface LivrableTangibleParTactique {
  id: string;
  pilierId: number;
  pilierNom: string;
  tactiqueId: string;
  tactiqueNom: string;
  livrableNom: string;
  description: string;
  type: 'Document' | 'Outil' | 'Dashboard' | 'Rapport' | 'Matrice' | 'Présentation' | 'Processus';
  standard: 'ITIL' | 'COBIT' | 'NIST' | 'ISO' | 'PMBOK' | 'RACI' | 'Agile' | 'Scrum' | 'Kanban' | 'DevOps' | 'BZ' | 'Formation' | 'Autre';
  profondeur: 'Humaine' | 'Procédurale' | 'Technologique' | 'Mixte';
  mentionneSolutionsAffaires: boolean;
}

// Mapping des termes "Solutions d'Affaires" vers les livrables PRIsME
const solutionsAffairesMapping: Record<string, string[]> = {
  'audit de sécurité': [
    'Audit de sécurité',
    'Évaluation sécurité',
    'Rapport audit',
    'Conformité NIST',
    'Conformité ISO 27001',
    'Plan remédiation sécurité'
  ],
  'bilan de santé': [
    'Bilan de santé TI',
    'Diagnostic TI',
    'Évaluation maturité',
    'Analyse posture TI'
  ],
  'comité ti': [
    'Comité TI',
    'Gouvernance TI',
    'Procès-verbal comité',
    'Présentation comité',
    'Livre minutes'
  ],
  'cahier de charge': [
    "Spécifications d'exigences d'affaires et TI",
    "Spécifications d'exigences",
    'Plan d\'action',
    'Document projet'
  ],
  'plan d\'action': [
    'Plan d\'action',
    "Spécifications d'exigences",
    'Roadmap',
    'Feuille de route'
  ]
};

/**
 * Vérifie si un livrable est mentionné dans "Solutions d'Affaires"
 */
function estMentionneSolutionsAffaires(livrableNom: string): boolean {
  const nomLower = livrableNom.toLowerCase();
  
  for (const [terme, correspondances] of Object.entries(solutionsAffairesMapping)) {
    if (nomLower.includes(terme)) {
      return true;
    }
    for (const correspondance of correspondances) {
      if (nomLower.includes(correspondance.toLowerCase())) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Détermine la profondeur du livrable (NIST-style)
 */
function determinerProfondeur(
  livrableNom: string,
  tactiqueNom: string,
  description?: string
): LivrableTangibleParTactique['profondeur'] {
  const nomLower = livrableNom.toLowerCase();
  const tactiqueLower = tactiqueNom.toLowerCase();
  const descLower = description?.toLowerCase() || '';
  const texteComplet = `${nomLower} ${tactiqueLower} ${descLower}`;
  
  // Indicateurs Humaine
  const indicateursHumaine = [
    'formation', 'sensibilisation', 'coaching', 'accompagnement', 'utilisateur',
    'employé', 'équipe', 'comité', 'gouvernance', 'communication', 'culture',
    'changement', 'adoption', 'satisfaction', 'perception', 'feedback'
  ];
  
  // Indicateurs Procédurale
  const indicateursProcedurale = [
    'processus', 'procédure', 'workflow', 'méthodologie', 'cadre', 'standard',
    'politique', 'règlement', 'guide', 'checklist', 'template', 'modèle',
    'audit', 'conformité', 'gouvernance', 'règles', 'normes'
  ];
  
  // Indicateurs Technologique
  const indicateursTechnologique = [
    'système', 'plateforme', 'outil', 'logiciel', 'application', 'infrastructure',
    'serveur', 'réseau', 'cloud', 'sécurité', 'firewall', 'antivirus', 'backup',
    'monitoring', 'dashboard', 'api', 'intégration', 'automatisation', 'ia',
    'base de données', 'cmdb', 'iam', 'sso', 'edr', 'siem'
  ];
  
  const scoreHumaine = indicateursHumaine.filter(ind => texteComplet.includes(ind)).length;
  const scoreProcedurale = indicateursProcedurale.filter(ind => texteComplet.includes(ind)).length;
  const scoreTechnologique = indicateursTechnologique.filter(ind => texteComplet.includes(ind)).length;
  
  const maxScore = Math.max(scoreHumaine, scoreProcedurale, scoreTechnologique);
  
  if (maxScore === 0) {
    // Par défaut, analyser le contexte de la tactique
    if (tactiqueLower.includes('formation') || tactiqueLower.includes('communication') || tactiqueLower.includes('satisfaction')) {
      return 'Humaine';
    } else if (tactiqueLower.includes('processus') || tactiqueLower.includes('gouvernance') || tactiqueLower.includes('audit')) {
      return 'Procédurale';
    } else if (tactiqueLower.includes('infrastructure') || tactiqueLower.includes('système') || tactiqueLower.includes('technologie')) {
      return 'Technologique';
    }
    return 'Mixte';
  }
  
  // Si un score domine clairement (>2x les autres)
  if (scoreHumaine > scoreProcedurale * 2 && scoreHumaine > scoreTechnologique * 2) {
    return 'Humaine';
  }
  if (scoreProcedurale > scoreHumaine * 2 && scoreProcedurale > scoreTechnologique * 2) {
    return 'Procédurale';
  }
  if (scoreTechnologique > scoreHumaine * 2 && scoreTechnologique > scoreProcedurale * 2) {
    return 'Technologique';
  }
  
  // Sinon, Mixte
  return 'Mixte';
}

/**
 * Détermine le type de livrable et le standard associé
 * Amélioré pour détecter tous les standards pertinents (RACI, Agile, Scrum, etc.)
 */
function determinerTypeEtStandard(
  livrableNom: string,
  tactiqueNom: string,
  livrableVisuel?: { type: string; description: string }
): { type: LivrableTangibleParTactique['type']; standard: LivrableTangibleParTactique['standard'] } {
  const nomLower = livrableNom.toLowerCase();
  const tactiqueLower = tactiqueNom.toLowerCase();
  const description = livrableVisuel?.description?.toLowerCase() || '';
  const texteComplet = `${nomLower} ${tactiqueLower} ${description}`;
  
  // Déterminer le type
  let type: LivrableTangibleParTactique['type'] = 'Document';
  if (livrableVisuel?.type) {
    const typeMap: Record<string, LivrableTangibleParTactique['type']> = {
      'Dashboard': 'Dashboard',
      'Rapport': 'Rapport',
      'Matrice': 'Matrice',
      'Présentation': 'Présentation',
      'Processus': 'Processus',
      'Outil': 'Outil',
      'Document': 'Document'
    };
    type = typeMap[livrableVisuel.type] || 'Document';
  } else if (nomLower.includes('dashboard') || description.includes('dashboard')) {
    type = 'Dashboard';
  } else if (nomLower.includes('rapport') || description.includes('rapport')) {
    type = 'Rapport';
  } else if (nomLower.includes('matrice') || description.includes('matrice')) {
    type = 'Matrice';
  } else if (nomLower.includes('présentation') || description.includes('présentation')) {
    type = 'Présentation';
  } else if (nomLower.includes('processus') || description.includes('processus') || description.includes('workflow')) {
    type = 'Processus';
  } else if (nomLower.includes('outil') || nomLower.includes('plateforme') || nomLower.includes('système')) {
    type = 'Outil';
  }
  
  // Déterminer le standard - Recherche exhaustive et priorisée
  // On essaie de trouver un standard pour TOUS les livrables (réduction "Autre")
  let standard: LivrableTangibleParTactique['standard'] = 'Autre';
  
  // 1. Formation (priorité haute - aspect humain critique)
  if (texteComplet.includes('formation') || texteComplet.includes('training') || 
      texteComplet.includes('sensibilisation') || texteComplet.includes('coaching') ||
      texteComplet.includes('apprentissage') || texteComplet.includes('éducation') ||
      texteComplet.includes('plan de formation') || texteComplet.includes('programme de formation') ||
      texteComplet.includes('curriculum') || texteComplet.includes('module de formation') ||
      nomLower.includes('formation') || nomLower.includes('training')) {
    standard = 'Formation';
  }
  // 2. Standards de gouvernance et processus (RACI)
  else if (texteComplet.includes('raci') || texteComplet.includes('responsible') || 
           texteComplet.includes('accountable') || texteComplet.includes('consulted') || 
           texteComplet.includes('informed') || 
           (nomLower.includes('matrice') && (nomLower.includes('responsabilité') || nomLower.includes('rôle'))) ||
           nomLower.includes('gouvernance') && (nomLower.includes('rôle') || nomLower.includes('responsabilité'))) {
    standard = 'RACI';
  }
  // 3. ITIL (gestion services TI)
  else if (texteComplet.includes('itil') || texteComplet.includes('service desk') || 
           texteComplet.includes('incident management') || texteComplet.includes('change management') ||
           texteComplet.includes('problem management') || texteComplet.includes('service level') ||
           texteComplet.includes('cmdb') || texteComplet.includes('configuration management') ||
           texteComplet.includes('service request') || texteComplet.includes('sla') ||
           texteComplet.includes('availability') || texteComplet.includes('capacity management') ||
           nomLower.includes('support') || nomLower.includes('ticket')) {
    standard = 'ITIL';
  }
  // 4. COBIT (gouvernance TI)
  else if (texteComplet.includes('cobit') || 
           (texteComplet.includes('gouvernance') && (texteComplet.includes('audit') || 
            texteComplet.includes('conformité') || texteComplet.includes('risque') || 
            texteComplet.includes('contrôle'))) ||
           texteComplet.includes('audit ti') || texteComplet.includes('évaluation gouvernance')) {
    standard = 'COBIT';
  }
  // 5. NIST (cybersécurité)
  else if (texteComplet.includes('nist') || texteComplet.includes('cybersécurité') || 
           texteComplet.includes('cybersecurity') || texteComplet.includes('edr') || 
           texteComplet.includes('siem') || texteComplet.includes('mfa') ||
           texteComplet.includes('audit de sécurité') || texteComplet.includes('conformité nist') ||
           texteComplet.includes('phishing') || texteComplet.includes('vulnérabilité') ||
           texteComplet.includes('patch') || texteComplet.includes('antivirus') ||
           nomLower.includes('sécurité') || nomLower.includes('cyber')) {
    standard = 'NIST';
  }
  // 6. ISO (certification)
  else if (texteComplet.includes('iso') || texteComplet.includes('27001') || 
           texteComplet.includes('certification') || texteComplet.includes('smsi') ||
           texteComplet.includes('audit iso') || texteComplet.includes('conformité iso')) {
    standard = 'ISO';
  }
  // 7. PMBOK/PMI (gestion projets)
  else if (texteComplet.includes('pmbok') || texteComplet.includes('pmi') || 
           texteComplet.includes('project management') || texteComplet.includes('gestion de projet') ||
           texteComplet.includes("spécifications d'exigences") || texteComplet.includes("Spécifications d'exigences") ||
           texteComplet.includes('wbs') || texteComplet.includes('work breakdown structure') ||
           texteComplet.includes('jalon') || texteComplet.includes('gantt') ||
           texteComplet.includes('portefeuille') || texteComplet.includes('roadmap') ||
           nomLower.includes('projet') || nomLower.includes('planification')) {
    standard = 'PMBOK';
  }
  // 8. Agile/Scrum
  else if (texteComplet.includes('scrum') || texteComplet.includes('sprint') || 
           texteComplet.includes('product owner') || texteComplet.includes('scrum master') ||
           texteComplet.includes('backlog') && texteComplet.includes('sprint')) {
    standard = 'Scrum';
  }
  else if (texteComplet.includes('agile') || texteComplet.includes('sprint planning') ||
           texteComplet.includes('daily standup') || texteComplet.includes('rétrospective') ||
           texteComplet.includes('user story') || texteComplet.includes('epic')) {
    standard = 'Agile';
  }
  // 9. Kanban
  else if (texteComplet.includes('kanban') || 
           (texteComplet.includes('board') && (texteComplet.includes('backlog') || 
            texteComplet.includes('en cours') || texteComplet.includes('done')))) {
    standard = 'Kanban';
  }
  // 10. DevOps
  else if (texteComplet.includes('devops') || texteComplet.includes('ci/cd') || 
           texteComplet.includes('continuous integration') || texteComplet.includes('continuous deployment') ||
           texteComplet.includes('pipeline') || texteComplet.includes('docker') || 
           texteComplet.includes('kubernetes') || texteComplet.includes('container') ||
           texteComplet.includes('git') || texteComplet.includes('jenkins')) {
    standard = 'DevOps';
  }
  // 11. BZ (standards BZ spécifiques)
  else if (texteComplet.includes('prisme') || texteComplet.includes('cadre bz') || 
           texteComplet.includes('panorama') || texteComplet.includes('aswas') || 
           texteComplet.includes('asis') || texteComplet.includes('target') ||
           texteComplet.includes('tactice') || texteComplet.includes('comité ti') ||
           (nomLower.includes('livre') && nomLower.includes('minutes')) ||
           nomLower.includes('besoin') || nomLower.includes('alignement')) {
    standard = 'BZ';
  }
  // 12. Analyse contextuelle approfondie (pour éviter "Autre")
  else {
    // Analyser le contexte de la tactique ET du livrable pour deviner le standard
    const contexteComplet = `${tactiqueLower} ${nomLower} ${description}`;
    
    // Formation/Humain
    if (contexteComplet.includes('formation') || contexteComplet.includes('training') ||
        contexteComplet.includes('sensibilisation') || contexteComplet.includes('coaching') ||
        contexteComplet.includes('utilisateur') || contexteComplet.includes('employé') ||
        contexteComplet.includes('satisfaction') || contexteComplet.includes('culture') ||
        contexteComplet.includes('communication') || contexteComplet.includes('adoption')) {
      standard = 'Formation';
    }
    // Gouvernance/RACI
    else if (contexteComplet.includes('gouvernance') || contexteComplet.includes('comité') || 
             contexteComplet.includes('décision') || contexteComplet.includes('responsabilité') ||
             contexteComplet.includes('rôle') || contexteComplet.includes('matrice')) {
      standard = 'RACI';
    }
    // Projets/PMBOK
    else if (contexteComplet.includes('projet') || contexteComplet.includes('planification') ||
             contexteComplet.includes('budget') || contexteComplet.includes('roadmap') ||
             contexteComplet.includes('portefeuille') || contexteComplet.includes('priorisation') ||
             contexteComplet.includes('jalon') || contexteComplet.includes('échéance')) {
      standard = 'PMBOK';
    }
    // Sécurité/NIST
    else if (contexteComplet.includes('sécurité') || contexteComplet.includes('cyber') ||
             contexteComplet.includes('audit') || contexteComplet.includes('conformité') ||
             contexteComplet.includes('risque') || contexteComplet.includes('vulnérabilité') ||
             contexteComplet.includes('patch') || contexteComplet.includes('sécuritaire')) {
      standard = 'NIST';
    }
    // Support/ITIL
    else if (contexteComplet.includes('support') || contexteComplet.includes('incident') ||
             contexteComplet.includes('service') || contexteComplet.includes('opération') ||
             contexteComplet.includes('ticket') || contexteComplet.includes('résolution') ||
             contexteComplet.includes('disponibilité') || contexteComplet.includes('sla')) {
      standard = 'ITIL';
    }
    // Infrastructure/Technologique - souvent ITIL ou DevOps
    else if (contexteComplet.includes('infrastructure') || contexteComplet.includes('système') ||
             contexteComplet.includes('serveur') || contexteComplet.includes('réseau') ||
             contexteComplet.includes('cloud') || contexteComplet.includes('backup')) {
      standard = 'ITIL'; // ITIL couvre l'infrastructure
    }
    // Actifs/Inventaire - souvent ITIL (CMDB)
    else if (contexteComplet.includes('actif') || contexteComplet.includes('inventaire') ||
             contexteComplet.includes('licence') || contexteComplet.includes('utilisateur') ||
             contexteComplet.includes('cmdb') || contexteComplet.includes('catalogue')) {
      standard = 'ITIL';
    }
    // KPI/Métriques - souvent BZ ou ITIL
    else if (contexteComplet.includes('kpi') || contexteComplet.includes('métrique') ||
             contexteComplet.includes('dashboard') || contexteComplet.includes('indicateur') ||
             contexteComplet.includes('performance') || contexteComplet.includes('mesure')) {
      standard = 'BZ'; // BZ a une approche forte sur les KPI
    }
    // Documentation/Processus - souvent ITIL ou BZ
    else if (contexteComplet.includes('documentation') || contexteComplet.includes('processus') ||
             contexteComplet.includes('procédure') || contexteComplet.includes('guide') ||
             contexteComplet.includes('template') || contexteComplet.includes('modèle')) {
      standard = 'ITIL'; // ITIL couvre la documentation des processus
    }
    // Rapport/Analyse - souvent BZ ou COBIT
    else if (nomLower.includes('rapport') || nomLower.includes('analyse') ||
             nomLower.includes('évaluation') || nomLower.includes('audit')) {
      // Si c'est un audit de sécurité → NIST, sinon COBIT pour gouvernance
      if (contexteComplet.includes('sécurité') || contexteComplet.includes('cyber')) {
        standard = 'NIST';
      } else if (contexteComplet.includes('gouvernance') || contexteComplet.includes('conformité')) {
        standard = 'COBIT';
      } else {
        standard = 'BZ'; // Rapports BZ par défaut
      }
    }
    // Par défaut final - essayer de deviner selon le pilier
    else {
      // Analyser selon le pilier pour dernier recours
      if (tactiqueLower.includes('p1') || tactiqueLower.includes('besoin') || 
          tactiqueLower.includes('alignement') || tactiqueLower.includes('stratégie')) {
        standard = 'BZ';
      } else if (tactiqueLower.includes('p2') || tactiqueLower.includes('actif') || 
                 tactiqueLower.includes('utilisateur') || tactiqueLower.includes('inventaire')) {
        standard = 'ITIL';
      } else if (tactiqueLower.includes('p3') || tactiqueLower.includes('projet') || 
                 tactiqueLower.includes('budget') || tactiqueLower.includes('portefeuille')) {
        standard = 'PMBOK';
      } else if (tactiqueLower.includes('p4') || tactiqueLower.includes('support') || 
                 tactiqueLower.includes('opération') || tactiqueLower.includes('service')) {
        standard = 'ITIL';
      } else if (tactiqueLower.includes('p5') || tactiqueLower.includes('sécurité') || 
                 tactiqueLower.includes('cyber')) {
        standard = 'NIST';
      } else if (tactiqueLower.includes('p6') || tactiqueLower.includes('innovation') || 
                 tactiqueLower.includes('amélioration')) {
        standard = 'BZ';
      } else if (tactiqueLower.includes('p7') || tactiqueLower.includes('ia') || 
                 tactiqueLower.includes('programmation')) {
        standard = 'BZ';
      } else {
        // Dernier recours - utiliser le type de livrable
        if (type === 'Dashboard' || type === 'Rapport') {
          standard = 'BZ';
        } else if (type === 'Processus' || type === 'Document') {
          standard = 'ITIL';
        } else if (type === 'Matrice') {
          standard = 'RACI';
        } else {
          standard = 'BZ'; // BZ par défaut plutôt que "Autre"
        }
      }
    }
  }
  
  return { type, standard };
}

/**
 * Génère une description détaillée pour un livrable
 */
function genererDescription(
  livrableNom: string,
  tactiqueNom: string,
  livrableVisuel?: { type: string; description: string }
): string {
  if (livrableVisuel?.description) {
    return livrableVisuel.description;
  }
  
  // Description générique basée sur le nom
  const nomLower = livrableNom.toLowerCase();
  if (nomLower.includes('rapport')) {
    return `Document structuré présentant les résultats et analyses liés à "${tactiqueNom}". Format professionnel avec données quantitatives, graphiques et recommandations actionnables.`;
  } else if (nomLower.includes('matrice')) {
    return `Tableau de bord visuel permettant l'analyse comparative et la prise de décision pour "${tactiqueNom}". Format Excel ou tableau interactif.`;
  } else if (nomLower.includes('dashboard')) {
    return `Tableau de bord temps réel affichant les métriques et KPIs liés à "${tactiqueNom}". Interface web ou outil de BI avec mises à jour automatiques.`;
  } else if (nomLower.includes('présentation')) {
    return `Présentation PowerPoint ou similaire structurant les informations clés de "${tactiqueNom}" pour communication direction ou comité TI.`;
  } else if (nomLower.includes('cahier') || nomLower.includes('charge')) {
    return `Document formel définissant les objectifs, périmètre, contraintes et livrables attendus pour "${tactiqueNom}". Template standardisé BZ conforme PMBOK.`;
  } else if (nomLower.includes('plan')) {
    return `Document de planification détaillant les étapes, échéances et responsabilités pour "${tactiqueNom}". Format structuré avec RACI et jalons.`;
  } else if (nomLower.includes('audit')) {
    return `Évaluation formelle de la conformité et de la maturité pour "${tactiqueNom}". Rapport détaillé avec écarts identifiés, risques et recommandations selon standards (NIST, ISO, COBIT).`;
  } else if (nomLower.includes('comité') || nomLower.includes('gouvernance')) {
    return `Document de gouvernance structurant les décisions, actions et suivi pour "${tactiqueNom}". Procès-verbal ou présentation pour comité TI.`;
  } else if (nomLower.includes('outil') || nomLower.includes('plateforme') || nomLower.includes('système')) {
    return `Outil logiciel ou plateforme technologique supportant "${tactiqueNom}". Solution configurée et opérationnelle avec documentation utilisateur.`;
  }
  
  return `Livrable tangible lié à "${tactiqueNom}". Document ou outil professionnel structuré selon les meilleures pratiques ITIL/COBIT/NIST.`;
}

/**
 * Génère la liste complète des livrables tangibles par tactique
 */
export function genererLivrablesTangiblesParTactique(): LivrableTangibleParTactique[] {
  const livrables: LivrableTangibleParTactique[] = [];
  
  tactiques.forEach((tactique) => {
    const pilier = piliers.find(p => p.id === tactique.pilier);
    const pilierNom = pilier?.nom || `Pilier ${tactique.pilier}`;
    
    // Ajouter les livrables textuels
    tactique.livrables.forEach((livrableNom) => {
      const { type, standard } = determinerTypeEtStandard(livrableNom, tactique.nom);
      const description = genererDescription(livrableNom, tactique.nom);
      const mentionneSolutionsAffaires = estMentionneSolutionsAffaires(livrableNom);
      const profondeur = determinerProfondeur(livrableNom, tactique.nom, description);
      
      livrables.push({
        id: `${tactique.id}-${livrableNom.replace(/\s+/g, '-').toLowerCase()}`,
        pilierId: tactique.pilier,
        pilierNom,
        tactiqueId: tactique.id,
        tactiqueNom: tactique.nom,
        livrableNom,
        description,
        type,
        standard,
        profondeur,
        mentionneSolutionsAffaires
      });
    });
    
    // Ajouter les livrables visuels
    tactique.livrablesVisuels?.forEach((livrableVisuel) => {
      const { type, standard } = determinerTypeEtStandard(livrableVisuel.nom, tactique.nom, livrableVisuel);
      const description = genererDescription(livrableVisuel.nom, tactique.nom, livrableVisuel);
      const mentionneSolutionsAffaires = estMentionneSolutionsAffaires(livrableVisuel.nom);
      const profondeur = determinerProfondeur(livrableVisuel.nom, tactique.nom, description);
      
      livrables.push({
        id: `${tactique.id}-${livrableVisuel.nom.replace(/\s+/g, '-').toLowerCase()}`,
        pilierId: tactique.pilier,
        pilierNom,
        tactiqueId: tactique.id,
        tactiqueNom: tactique.nom,
        livrableNom: livrableVisuel.nom,
        description,
        type,
        standard,
        profondeur,
        mentionneSolutionsAffaires
      });
    });
  });
  
  // Trier par pilier, puis par tactique
  livrables.sort((a, b) => {
    if (a.pilierId !== b.pilierId) {
      return a.pilierId - b.pilierId;
    }
    return a.tactiqueId.localeCompare(b.tactiqueId);
  });
  
  return livrables;
}

// Export de la liste complète
export const livrablesTangiblesParTactique = genererLivrablesTangiblesParTactique();

